/**
 * BonusesTab — Configuración de bonos de nómina (colación, movilización, etc.)
 */
import { useState } from 'react'
import { Plus, Pencil, ToggleLeft, ToggleRight, Users } from 'lucide-react'
import { useBonuses, useCreateBonus, PayrollBonus } from '../hooks/usePayroll'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { AccountAutocomplete } from '@/modules/accounting/components/AccountAutocomplete'
import { BulkAssignModal } from './BulkAssignModal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import { usePermission } from '@/hooks/usePermission'
import { ToastContainer, useToast } from '@/components/Toast'
import { Modal } from '@/components/ds/Modal'
import { extractFieldErrors, extractErrorMessage } from '@/lib/errorUtils'
import { inputCls, labelCls } from '@/lib/formStyles'
import { useForm } from 'react-hook-form'
import { EmptyState } from '@/components/EmptyState'
import { DataTable } from '@/components/ds/DataTable'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

const BONUS_TYPE_LABELS: Record<string, string> = {
  fixed:      'Fijo',
  percentage: 'Porcentaje',
  variable:   'Variable',
}

interface BonusForm {
  name: string
  code: string
  bonus_type: string
  default_amount: string
  is_taxable: boolean
  is_imponible: boolean
  prorate_by_days: string
  is_active: boolean
}

function useUpdateBonus(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (d: Partial<PayrollBonus>) => {
      const r = await apiClient.patch(`/payroll/bonuses/${id}/`, d)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-bonuses'] }),
  })
}

function BonusModal({ initial, onClose }: { initial?: PayrollBonus; onClose: () => void }) {
  const toast = useToast()
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm<BonusForm>({
    defaultValues: {
      name: initial?.name ?? '',
      code: initial?.code ?? '',
      bonus_type: initial?.bonus_type ?? 'fixed',
      default_amount: initial?.default_amount ?? '0',
      is_taxable: initial?.is_taxable ?? true,
      is_imponible: initial?.is_imponible ?? true,
      prorate_by_days: initial?.prorate_by_days ?? '',
      is_active: initial?.is_active ?? true,
    },
  })

  const createBonus = useCreateBonus()
  const updateBonus = useUpdateBonus(initial?.id ?? '')
  const { data: accountsData } = useAccounts({ is_imputable: 'true', is_active: 'true' })
  const accounts = accountsData?.accounts ?? []
  const [account, setAccount] = useState<string | null>(initial?.account ?? null)

  const onSubmit = async (data: BonusForm) => {
    try {
      if (initial) {
        await updateBonus.mutateAsync({ ...data, account } as any)
        toast.success('Bono actualizado')
      } else {
        await createBonus.mutateAsync({ ...data, account } as any)
        toast.success('Bono creado')
      }
      onClose()
    } catch (e: any) {
      const fields = extractFieldErrors(e)
      if (fields) {
        Object.entries(fields).forEach(([k, v]) => setError(k as any, { message: v }))
      } else {
        toast.error(extractErrorMessage(e, 'Error al guardar bono'))
      }
    }
  }

  return (
    <Modal open title={initial ? 'Editar Bono' : 'Nuevo Bono'} onClose={onClose} size="sm"
      actions={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft">Cancelar</button>
          <button type="submit" form="bonus-form" disabled={isSubmitting} className="px-4 py-2 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-50">
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      }
    >
      <form id="bonus-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            dense
            label="Nombre *"
            placeholder="Colación"
            inputProps={register('name', { required: 'Requerido' })}
            error={errors.name?.message}
          />
          <TextField
            dense
            label="Código *"
            placeholder="COLACION"
            inputProps={register('code', { required: 'Requerido' })}
            error={errors.code?.message}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <SelectField
            dense
            label="Tipo"
            selectProps={register('bonus_type')}
          >
            <option value="fixed">Fijo</option>
            <option value="percentage">Porcentaje</option>
            <option value="variable">Variable</option>
          </SelectField>
          <TextField
            dense
            label="Monto por defecto"
            placeholder="0"
            inputProps={{ ...register('default_amount'), type: 'number', step: '1', min: '0' }}
            error={errors.default_amount?.message}
          />
        </div>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input {...register('is_taxable')} type="checkbox" className="rounded" />
            Afecto a impuesto
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input {...register('is_imponible')} type="checkbox" className="rounded" />
            Imponible (AFP/Salud)
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input {...register('is_active')} type="checkbox" className="rounded" />
            Activo
          </label>
        </div>

        <div>
          <label htmlFor="bonusestab-prorate-by-days" className={labelCls}>Mes parcial (licencia, ingreso o salida a mitad de mes)</label>
          <select id="bonusestab-prorate-by-days" {...register('prorate_by_days')} className={inputCls}>
            <option value="">Automático — según el tipo de bono</option>
            <option value="yes">Proporcional a los días trabajados</option>
            <option value="no">Siempre el monto completo</option>
          </select>
          <p className="text-xs text-content-subtle mt-1">
            En automático, un bono <strong>fijo</strong> se prorratea igual que el sueldo base
            (26 de 30 días de un bono de $30.000 son $26.000) y un bono <strong>variable</strong> se
            paga completo, porque el monto que se ingresa cada mes ya viene devengado.
          </p>
        </div>

        <div>
          <label htmlFor="bonusestab-cuenta-de-gasto-centralizacion" className={labelCls}>Cuenta de gasto (centralización)</label>
          <AccountAutocomplete id="bonusestab-cuenta-de-gasto-centralizacion" accounts={accounts} value={account} onChange={setAccount} />
          <p className="text-xs text-content-subtle mt-1">
            Opcional. Cuenta de gasto a la que se imputa el bono al centralizar la nómina.
            Vacío = se imputa a Remuneraciones junto al resto del sueldo.
          </p>
        </div>
      </form>
    </Modal>
  )
}

export default function BonusesTab() {
  const [showModal, setShowModal] = useState(false)
  const [showBulk, setShowBulk] = useState(false)
  const [editing, setEditing] = useState<PayrollBonus | undefined>()
  const { data, isLoading } = useBonuses()
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()

  const bonuses: PayrollBonus[] = data?.bonuses ?? []

  return (
    <>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />

      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-content-muted">{bonuses.length} bono{bonuses.length !== 1 ? 's' : ''} configurado{bonuses.length !== 1 ? 's' : ''}</p>
        <div className="flex items-center gap-2">
          {can('payroll.bonuses', 'create') && bonuses.length > 0 && (
            <button onClick={() => setShowBulk(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-divider rounded-lg hover:bg-soft text-content">
              <Users size={15} /> Asignar a empleados
            </button>
          )}
          {can('payroll.bonuses', 'create') && (
            <button onClick={() => { setEditing(undefined); setShowModal(true) }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
              <Plus size={15} /> Nuevo Bono
            </button>
          )}
        </div>
      </div>
      {showBulk && <BulkAssignModal mode="bonus" toast={toast} onClose={() => setShowBulk(false)} />}

      <DataTable<PayrollBonus>
        columns={[
          { key: 'name', header: 'Nombre', sortable: true,
            render: b => <span className="font-medium text-content">{b.name}</span> },
          { key: 'code', header: 'Código', sortable: true,
            render: b => <span className="font-mono text-xs text-content-muted">{b.code}</span> },
          { key: 'bonus_type', header: 'Tipo', sortable: true,
            sortValue: b => BONUS_TYPE_LABELS[b.bonus_type] ?? b.bonus_type,
            render: b => (
              <span className="text-content-muted">{BONUS_TYPE_LABELS[b.bonus_type] ?? b.bonus_type}</span>
            ) },
          { key: 'default_amount', header: 'Monto Defecto', numeric: true, sortable: true,
            sortValue: b => Number(b.default_amount),
            render: b => formatNumber(Number(b.default_amount), locale, 0) },
          { key: 'account_code', header: 'Cuenta de gasto', sortable: true,
            render: b => (
              <span className="text-xs">
                {b.account_code
                  ? <span className="text-content-muted">{b.account_code} — {b.account_name}</span>
                  : <span className="text-content-subtle">Remuneraciones</span>}
              </span>
            ) },
          { key: 'prorates_by_days', header: 'Mes parcial', sortable: true,
            sortValue: b => (b.prorates_by_days ? 1 : 0),
            render: b => (
              <span className={`text-xs ${b.prorates_by_days ? 'text-content-muted' : 'text-content-subtle'}`}
                title={b.prorate_by_days
                  ? 'Configurado a mano en la ficha del bono.'
                  : `Automático: los bonos de tipo «${BONUS_TYPE_LABELS[b.bonus_type] ?? b.bonus_type}» ${b.prorates_by_days ? 'se prorratean' : 'se pagan completos'}.`}>
                {b.prorates_by_days ? 'Proporcional' : 'Completo'}
                {!b.prorate_by_days && <span className="text-content-subtle"> (auto)</span>}
              </span>
            ) },
          { key: 'is_imponible', header: 'Imponible', sortable: true,
            sortValue: b => (b.is_imponible ? 1 : 0),
            render: b => (b.is_imponible
              ? <ToggleRight size={18} className="text-success-solid mx-auto" />
              : <ToggleLeft size={18} className="text-content-subtle mx-auto" />) },
          { key: 'is_taxable', header: 'Afecto Imp.', sortable: true,
            sortValue: b => (b.is_taxable ? 1 : 0),
            render: b => (b.is_taxable
              ? <ToggleRight size={18} className="text-success-solid mx-auto" />
              : <ToggleLeft size={18} className="text-content-subtle mx-auto" />) },
          { key: 'is_active', header: 'Estado', sortable: true,
            sortValue: b => (b.is_active ? 1 : 0),
            render: b => (
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${b.is_active ? 'bg-success-surface-strong text-success-content' : 'bg-surface-muted text-content-muted'}`}>
                {b.is_active ? 'Activo' : 'Inactivo'}
              </span>
            ) },
          ...(can('payroll.bonuses', 'edit') ? [{
            key: 'acciones', header: '', width: 56,
            render: (b: PayrollBonus) => (
              <button onClick={() => { setEditing(b); setShowModal(true) }}
                className="p-1.5 rounded hover:bg-soft text-content-subtle hover:text-content-muted">
                <Pencil size={14} />
              </button>
            ),
          }] : []),
        ]}
        rows={bonuses}
        rowKey={b => b.id}
        minWidth={1100}
        loading={isLoading}
        empty={
          <EmptyState
            title="No hay bonos configurados."
            hint="Crea bonos para asignarlos a empleados (colación, movilización, etc.)"
            size="md"
          />
        }
        caption="Bonos configurados para la nómina"
      />

      {showModal && (
        <BonusModal
          initial={editing}
          onClose={() => { setShowModal(false); setEditing(undefined) }}
        />
      )}
    </>
  )
}
